import { createSlice, createSelector, PayloadAction } from "@reduxjs/toolkit";

interface MultiValueFilter {
  value: string[];
  displayValue: string[];
}

export interface TasksFilterState {
  dueToday: boolean;
  overdue: boolean;
  upcoming: boolean;
  completed: boolean;
  assigneeIds: MultiValueFilter;
  propertyIds: MultiValueFilter;
  cityIds: MultiValueFilter;
  countryIds: MultiValueFilter;
}

const emptyMultiValue: MultiValueFilter = { value: [], displayValue: [] };

const initialState: TasksFilterState = {
  dueToday: false,
  overdue: true,
  upcoming: true,
  completed: false,
  assigneeIds: { ...emptyMultiValue },
  propertyIds: { ...emptyMultiValue },
  cityIds: { ...emptyMultiValue },
  countryIds: { ...emptyMultiValue },
};

const resetState: TasksFilterState = {
  dueToday: false,
  overdue: false,
  upcoming: false,
  completed: false,
  assigneeIds: { ...emptyMultiValue },
  propertyIds: { ...emptyMultiValue },
  cityIds: { ...emptyMultiValue },
  countryIds: { ...emptyMultiValue },
};

const tasksFilterSlice = createSlice({
  name: "tasksFilter",
  initialState,
  reducers: {
    setTasksDueToday(state, action: PayloadAction<boolean>) {
      state.dueToday = action.payload;
    },
    setTasksOverdue(state, action: PayloadAction<boolean>) {
      state.overdue = action.payload;
    },
    setTasksUpcoming(state, action: PayloadAction<boolean>) {
      state.upcoming = action.payload;
    },
    setTasksCompleted(state, action: PayloadAction<boolean>) {
      state.completed = action.payload;
    },
    setTasksAssigneeIds(state, action: PayloadAction<MultiValueFilter>) {
      state.assigneeIds = action.payload;
    },
    clearTasksAssigneeIds(state) {
      state.assigneeIds = { ...emptyMultiValue };
    },
    setTasksPropertyIds(state, action: PayloadAction<MultiValueFilter>) {
      state.propertyIds = action.payload;
    },
    clearTasksPropertyIds(state) {
      state.propertyIds = { ...emptyMultiValue };
    },
    setTasksCityIds(state, action: PayloadAction<MultiValueFilter>) {
      state.cityIds = action.payload;
    },
    clearTasksCityIds(state) {
      state.cityIds = { ...emptyMultiValue };
    },
    setTasksCountryIds(state, action: PayloadAction<MultiValueFilter>) {
      state.countryIds = action.payload;
    },
    clearTasksCountryIds(state) {
      state.countryIds = { ...emptyMultiValue };
    },
    clearAllTasksFilters() {
      return resetState;
    },
  },
});

export const {
  setTasksDueToday,
  setTasksOverdue,
  setTasksUpcoming,
  setTasksCompleted,
  setTasksAssigneeIds,
  clearTasksAssigneeIds,
  setTasksPropertyIds,
  clearTasksPropertyIds,
  setTasksCityIds,
  clearTasksCityIds,
  setTasksCountryIds,
  clearTasksCountryIds,
  clearAllTasksFilters,
} = tasksFilterSlice.actions;

export const selectTasksFilters = (state: {
  tasksFilter: TasksFilterState;
}) => state.tasksFilter;

export const selectTasksActiveFilterCount = createSelector(
  selectTasksFilters,
  (filters) => {
    let count = 0;
    if (filters.dueToday) count++;
    if (filters.overdue) count++;
    if (filters.upcoming) count++;
    if (filters.completed) count++;
    if (filters.assigneeIds.value.length > 0) count++;
    if (filters.propertyIds.value.length > 0) count++;
    if (filters.cityIds.value.length > 0) count++;
    if (filters.countryIds.value.length > 0) count++;
    return count;
  }
);

export const enableClearAllTasksFilters = createSelector(
  selectTasksFilters,
  (filters) =>
    filters.dueToday ||
    filters.overdue ||
    filters.upcoming ||
    filters.completed ||
    filters.assigneeIds.value.length > 0 ||
    filters.propertyIds.value.length > 0 ||
    filters.cityIds.value.length > 0 ||
    filters.countryIds.value.length > 0
);

export default tasksFilterSlice.reducer;
